// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useDeviceSizeStore from '../../../../../app/store/deviceSize';
// Components
import { Button } from '../../../ui/button';
import Icon from '../../icon/Icon';
import ICONS from '../../../../enums/icons';
import Switch from '../../../ui/switch';

/**
 * @param root0
 * @param root0.columns
 * @param root0.columnsToDisplay
 * @param root0.onClose
 * @param root0.handleColumnOrderChange
 * @param root0.setColumnsManagerOpen
 * @returns {JSX} ColumnsManager
 */
function ColumnsManager({ columns, columnsToDisplay, handleColumnOrderChange, setColumnsManagerOpen }) {
    // State
    const [columnsListToOrder, setColumnsListToOrder] = useState(columnsToDisplay);
    const [dragStart, setDragStart] = useState(null);
    const [dragEnter, setDragEnter] = useState(null);
    const [iconClickedTargetStart, setIconClickedTargetStart] = useState(false);

    // Store
    const isSmallDevice = useDeviceSizeStore(s => s.isSmallDevice);

    // Hooks
    useEffect(() => {
        if (dragEnter !== null) {
            const columnsListToOrderCopy = [...columnsListToOrder];
            columnsListToOrderCopy[dragStart] = columnsListToOrderCopy.splice(dragEnter, 1, columnsListToOrderCopy[dragStart])[0];
            setColumnsListToOrder(columnsListToOrderCopy);
            handleColumnOrderChange(columnsListToOrderCopy);
            setDragStart(dragEnter);
        }
    }, [dragEnter]);

    const handleDragStart = (position) => {
        setDragStart(position);
    };
    const handleDragEnter = (position) => {
        setDragEnter(position);
    };
    const handleDragEnd = () => {
        setDragStart(null);
        setDragEnter(null);
    };
    const handleMobileMoveStart = (position) => {
        setIconClickedTargetStart(true);
        setDragStart(position);
    };
    const handleMobileMoveEnd = (position) => {
        setIconClickedTargetStart(false);
        setDragEnter(position);
    };
    const resetColumns = () => {
        const initialColumnOrder = (columns.map(col => col.id));
        setColumnsListToOrder(initialColumnOrder);
        handleColumnOrderChange(initialColumnOrder);
    };

    const validate = () => {
        handleColumnOrderChange(columnsListToOrder);
        setColumnsManagerOpen(false);
    };


    return (
        <div className='space-y-1'>
            {
                columnsToDisplay.map((column, index) => {
                    const columnFromTable = columns.find(x => x.id === column);
                    if (!columnFromTable || !columnFromTable.columnDef.entity) {
                        return null;
                    }
                    return (
                        <div id='draggable-columns-order'
                            className={`columns-container justify-between flex cursor-grab ${iconClickedTargetStart && dragStart === index ? 'mobile-touch' : ''}`}
                            key={column}
                            draggable={true}
                            onDragStart={() => handleDragStart(index)}
                            onDragEnter={() => handleDragEnter(index)}
                            onDragEnd={() => handleDragEnd()}
                            onDragOver={(e) => e.preventDefault()}
                        >
                            <Switch
                                name={`${column}`}
                                checked={columnFromTable.getIsVisible()}
                                onCheckedChange={(value) =>
                                    columnFromTable.toggleVisibility(!!value)
                                }
                                disabled={columnFromTable.columnDef && columnFromTable.columnDef.enableHiding === false}
                                entity={columnFromTable.columnDef.entity}
                                namespace={columnFromTable.columnDef.namespace}
                            />
                            <div>
                                <Icon
                                    icon={iconClickedTargetStart && dragStart === index ? ICONS.SWAP : ICONS.DRAG}
                                    onClick={() => {
                                        if (isSmallDevice && !iconClickedTargetStart) {
                                            handleMobileMoveStart(index);
                                        } else if (iconClickedTargetStart) {
                                            handleMobileMoveEnd(index);
                                        }
                                    }}
                                    className={'flex-1 text-end mr-2 mt-1'}
                                />
                            </div>
                        </div>
                    );
                }

                ).filter(el => !!el)
            }
            <div className='flex justify-between pt-1'>
                <Button
                    variant='secondary'
                    icon={ICONS.RESET}
                    entity='button.reset'
                    onClick={() => resetColumns()}
                    disabled={columnsListToOrder.every((column, index) => column === columns[index].id)}
                />
                <Button
                    variant='primary'
                    icon={ICONS.VALIDATE}
                    entity='button.submit'
                    onClick={() => validate()}
                    disabled={false}
                />
            </div>
        </div>
    );
}

ColumnsManager.propTypes = {
    columns: PropTypes.array.isRequired,
    columnsToDisplay: PropTypes.array.isRequired,
    handleColumnOrderChange: PropTypes.func.isRequired,
    setColumnsManagerOpen: PropTypes.func
};

export default ColumnsManager;